import { ToastOptions } from './types.js';

// Toast notification function
// Status notification using connection indicator
export function showStatusNotification(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', duration: number = 3000): void {
  const statusElements = document.querySelectorAll('.status-indicator');
  const statusTexts = document.querySelectorAll('#statusText, [id*="statusText"]');
  
  // Store original states
  const originalStates = Array.from(statusElements).map(element => ({
    element,
    originalClass: element.className,
    originalBackground: (element as HTMLElement).style.background
  }));
  
  const originalTexts = Array.from(statusTexts).map(element => ({
    element,
    originalText: element.textContent || ''
  }));

  // Apply notification styling
  const backgroundColor = getNotificationColor(type);
  
  statusElements.forEach(element => {
    (element as HTMLElement).style.background = backgroundColor;
    element.className = `status-indicator notification-${type}`;
  });
  
  statusTexts.forEach(element => {
    element.textContent = message;
  });

  // Restore after duration
  setTimeout(() => {
    originalStates.forEach(({ element, originalClass, originalBackground }) => {
      element.className = originalClass;
      (element as HTMLElement).style.background = originalBackground;
    });
    
    originalTexts.forEach(({ element, originalText }) => {
      element.textContent = originalText;
    });
  }, duration);
}

// Legacy function for backward compatibility - redirects to status notification
export async function showToast(options: ToastOptions): Promise<void> {
  const {
    type = 'info',
    message,
    duration = 3000
  } = options;

  showStatusNotification(message, type, duration);
}

function getNotificationColor(type: 'success' | 'error' | 'warning' | 'info'): string {
  switch (type) {
    case 'success':
      return '#28a745';
    case 'error':
      return '#dc3545';
    case 'warning':
      return '#ffc107';
    case 'info':
    default:
      return '#0066cc';
  }
}


// Debounce utility function
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// Throttle utility function
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// File size formatting
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Date formatting
export function formatDate(date: string | Date): string {
  const d = new Date(date);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}


// Local storage utilities
export const storage = {
  get: <T>(key: string, defaultValue?: T): T | null => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue || null;
    } catch {
      return defaultValue || null;
    }
  },
  
  set: <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  },
  
  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  },
  
  clear: (): void => {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }
};

// Network utilities
export const network = {
  isOnline: (): boolean => {
    return navigator.onLine;
  },
  
  addOnlineListener: (callback: () => void): void => {
    window.addEventListener('online', callback);
  },
  
  addOfflineListener: (callback: () => void): void => {
    window.addEventListener('offline', callback);
  },
  
  removeOnlineListener: (callback: () => void): void => {
    window.removeEventListener('online', callback);
  },
  
  removeOfflineListener: (callback: () => void): void => {
    window.removeEventListener('offline', callback);
  }
};

// Device detection
export const device = {
  isMobile: (): boolean => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  },

  isTablet: (): boolean => {
    return /iPad|Android(?=.*\bMobile\b)(?=.*\bSafari\b)/i.test(navigator.userAgent);
  },

  isTouch: (): boolean => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  },

  isSafari: (): boolean => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }
};

